// Modules
import React from "react";
//@ts-ignore
import { LocalizationContext } from "react-localize";
import MvLink from "../../../../Generic/MvLink";

// Styles
import "./UsageDetails.sass";

// Interfaces
import { ContractUsage } from "Interfaces/AYPResultInterface";
import ContractToolTip from "Interfaces/ContractToolTipInterface";

// Constants
import { RequestStatus, ScreenType } from "Constants";

//Components
import { MvSortTable } from "Generic/MvSortTable";

//Utils
import { sortContracts, SortType, SortDirection } from "Utils/SortContracts";
import LocalizationService from "Services/localization.service";

// @ts-ignore
import ToolTip from "Generic/ToolTip";
import { Tip } from "Interfaces/TipInterface";
import { Position } from "Generic/ToolTip/ToolTip";
import { ReservationType } from "Interfaces/GetReservations";
import moment from "moment";

type Props = {
  contracts: ContractUsage[];
  contractToolTips: ContractToolTip[];
  onContractsSort: (contracts: ContractUsage[]) => void;
  onEditClick: () => void;
  readOnly: boolean;
  setTablesOpen?: number;
  tablesExpandable?: boolean;
  tablesInitOpen?: boolean;
  resType?: ReservationType;
  screenType?: string;
  appliedPoints?: number;
  apiTimeout: string;
}

type UsageDetailsState = {
  tableSort: {
    [SortType.CONTRACT_SORT]: SortDirection;
    //the 2 below are not required, but leaving them in case we add them later
    [SortType.DATE_SORT]: SortDirection;
    [SortType.USAGE_TYPE_SORT]: SortDirection;
    [SortType.USAGE_ORDER]: SortDirection;
  }
  tablesExpanded: boolean;
}

const InitState: UsageDetailsState = {
  tableSort: {
    [SortType.CONTRACT_SORT]: SortDirection.ASC,
    [SortType.DATE_SORT]: SortDirection.ASC,
    [SortType.USAGE_TYPE_SORT]: SortDirection.ASC,
    [SortType.USAGE_ORDER]: SortDirection.ASC
  },
  tablesExpanded: false,
};

class UsageDetails extends React.Component<Props, UsageDetailsState> {

  pointsTip: [Tip] = [
    {
      label: "",
      desc: "static.content.tip.usage-detail-points",
    },
  ];

  localizationService: LocalizationService;

  constructor(props: Readonly<Props>) {
    super(props);

    const state = Object.assign({}, InitState);
    if (props.tablesInitOpen) {
      state.tablesExpanded = true;
    }

    this.state = {...state};
    this.localizationService = new LocalizationService();
  }

  componentWillReceiveProps(nProps: Props) {
    if (this.props.setTablesOpen !== nProps.setTablesOpen) {
      this.setState({ tablesExpanded: true });
    }
  }

  /**
   * return the array of headers for the usage details table
   */
  tableHeaders() {
    const { localize } = this.context;

    const headers=  [
      {
        text: localize("static.content.ayp-contract"),
        sortable: false,
        curSortDirection: this.state.tableSort[SortType.CONTRACT_SORT],
        onSort: () => {
          this.onSortClicked(SortType.CONTRACT_SORT);
        }
      },
      {
        text: localize("static.content.ayp-usage-type"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-usage-year"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-applied"),
        sortable: false,
      },
    ];

    if(this.props.screenType){
      headers.push(
        {
          text: localize("Usage order"),
          sortable: false,
        }
      )
    }

    return headers;
  }

  /**
     * return the array of headers for the usage details table for borrowed pts
     */
  otherTableHeaders() {
    const { localize } = this.context;

    const headers= [
      {
        text: localize("static.content.ayp-contract"),
        sortable: false,
        curSortDirection: this.state.tableSort[SortType.CONTRACT_SORT],
        onSort: () => {
          this.onSortClicked(SortType.CONTRACT_SORT);
        }
      },
      {
        text: localize("Transaction Type"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-usage-type"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-usage-year"),
        sortable: false,
      },
      {
        text: localize("Points"),
        sortable: false,
      },
    ];

    return headers;
  }

  /**
     * return the array of headers for the usage details table for refunded pts
     */
   refundTableHeaders() {
    const { localize } = this.context;

    return [
      {
        text: localize("static.content.ayp-contract"),
        sortable: false,
        curSortDirection: this.state.tableSort[SortType.CONTRACT_SORT],
        onSort: () => {
          this.onSortClicked(SortType.CONTRACT_SORT);
        }
      },
      {
        text: localize("static.content.ayp-usage-year"),
        sortable: false,
      },
      {
        text: localize("static.content.ayp-usage-type"),
        sortable: false,
      },
      {
        text: localize("Use Between"),
        sortable: false,
      },
      {
        text: localize("Points"),
        sortable: false,
      },
      {
        text: localize("Description"),
        sortable: false,
      },
    ];
  }

  /**
   * return the jsx for each cell in the usage details table
   */
  tableRows() {
    return (this.props.contracts || []).filter(contract => !contract.refundChecked).map(contract => {
      const { localize } = this.context;
      const applied = this.getFormattedPoint(contract.appliedPoints);
      const friendlyUsageTypeName = this.localizationService.getUsageTypeNameFromJahia(contract.usageType || contract.type).value;

      if (this.props.screenType) {
        return {
          rowKey: contract.usageId,
          rowCells: {
            contractId: this.getContractToolTip(contract.contractNumber, contract.usageId),
            usageType: <p aria-label={friendlyUsageTypeName}>{friendlyUsageTypeName}</p>,
            usageYear: <p aria-label={contract.usageYear || contract.year}>{contract.usageYear || contract.year}</p>,
            applied: <p aria-label={applied}>{applied}</p>,
            usageOrder: ''
          }
        };
      } else {
        return {
          rowKey: contract.usageId,
          rowCells: {
            contractId: this.getContractToolTip(contract.contractId, contract.usageId),
            usageType: <p aria-label={friendlyUsageTypeName}>{friendlyUsageTypeName}</p>,
            usageYear: <p aria-label={contract.usageYear || contract.year}>{contract.usageYear || contract.year}</p>,
            applied: <p aria-label={applied}>{applied}</p>
          }
        };
      }
    });
  }

  /**
 * return the jsx for each cell in the usage details table for borrowed pts
 */
  otherTableRows() {
    return (this.props.contracts || []).filter(contract => contract.borrowChecked).map(contract => {
      const { localize } = this.context;
      const applied = this.getFormattedPoint(contract.appliedPoints || contract.applied).toLocaleString();
      const friendlyUsageTypeName = this.localizationService.getUsageTypeNameFromJahia(contract.usageType || contract.type).value;

      return {
        rowKey: contract.usageId,
        rowCells: {
          contractId: this.getContractToolTip(contract.contractId, contract.usageId),
          transactionType: <p aria-label={'borrowed-pts'}>{this.localizationService.getTransactionTypeNameFromJahia('Borrow Points').value}</p>,
          usageType: <p aria-label={friendlyUsageTypeName}>{friendlyUsageTypeName}</p>,
          usageYear: <p aria-label={contract.usageYear || contract.year}>{contract.usageYear || contract.year}</p>,      
          applied: <p aria-label={applied}>{applied}</p>
        }
      };
    });
  }

  /**
 * return the jsx for each cell in the usage details table for refunded pts
 */
   refundTableRows() {
    return (this.props.contracts || []).filter(contract => contract.refundChecked).map(contract => {
      const { localize } = this.context;
      const applied = this.getFormattedPoint(contract.appliedPoints || contract.applied).toLocaleString();
      const friendlyUsageTypeName = this.localizationService.getUsageTypeNameFromJahia(contract.usageType || contract.type).value;

      return {
        rowKey: contract.usageId,
        rowCells: {
          contractId: this.getContractToolTip(contract.contractId, contract.usageId),
          usageYear: <p aria-label={contract.usageYear || contract.year}>{contract.usageYear || contract.year}</p>,      
          usageType: <p aria-label={friendlyUsageTypeName}>{friendlyUsageTypeName}</p>,
          useBy: <p aria-label={contract.startDate}>{`${moment(contract.startDate).format("MMM DD, YYYY")} - ${moment(contract.endDate).format("MMM DD, YYYY")}`}</p>,
          applied: <p aria-label={applied}>{applied}</p>,
          description: <p aria-label={contract.description}>{contract.description}</p>
        }
      };
    });
  }

  /**
  * fired when a column sort icon is clicked in the 'Usage Details' table
  */
  onSortClicked(sortField: SortType) {
    const { contracts } = this.props;
    const tableSort = this.state.tableSort;
    const sortDirection = tableSort[sortField] === SortDirection.ASC ?
      SortDirection.DESC : SortDirection.ASC;

    const sortedContracts = sortContracts(
      contracts,
      sortField,
      sortDirection
    );

    tableSort[sortField] = sortDirection;

    // reset other sort directions to ASC (all sort icons will return to ASC)
    if (sortField !== SortType.CONTRACT_SORT) {
      tableSort[SortType.CONTRACT_SORT] = SortDirection.ASC;
    }
    if (sortField !== SortType.DATE_SORT) {
      tableSort[SortType.DATE_SORT] = SortDirection.ASC;
    }

    //update contracts state on parent
    this.props.onContractsSort(sortedContracts);
  }

  /**
   * Gets the ContractToolTip for the specific contractId
   * If it can't be found, return the generic ToolTip
   */
  getContractToolTip(contractId: string, useageId: string): any {
    const { contractToolTips } = this.props;

    const contractToolTip = contractToolTips === null ? null : contractToolTips.find((ctt: ContractToolTip) => {
      return ctt.contractId === contractId;
    });

    return contractToolTip ? <div style={{ marginTop: '4px' }}>{contractToolTip.toolTip}</div> : this.getContractToolTipGeneric(contractId, useageId);
  }

  /**
   * Gets the ToolTip for the specific contractId
   */
  getContractToolTipGeneric(contractId: string, useageId: string): any {
    const { localize } = this.context;
    const tip: [Tip] = [{ label: `${localize("static.content.ayp-contract")}`, desc: `${contractId}` }];
    const contractIdStyle = <p key={useageId} className="contract-id mv-print-link" aria-label={contractId}>{contractId}</p>;

    return <div style={{ marginTop: '4px' }}><ToolTip useRefToPosition={false} id={`contract-generic-${contractId}`} tabIndex={-1} tip={tip} position={Position.RIGHT}>
      {contractIdStyle}
    </ToolTip></div>;
  }

  getFormattedPoint = (point: number): string => {
    return point.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  getTotalPointsApplied(): string {
    let totalPoint = this.props.appliedPoints;
    const resType = this.props.resType;

    if (!totalPoint){
      totalPoint = (this.props.contracts || []).filter(contract => !contract.refundChecked).map(c => c.appliedPoints).reduce((total, cur) => (
        total + cur
      ), 0);
    }

    //if this is a past or canceled reservation AND points = 0, return empty string, otherwise return formatted total points
    if ((resType === ReservationType.PAST || resType === ReservationType.CANCELED) && totalPoint === 0){
      return '';
    }
    else {
      return this.getFormattedPoint(totalPoint);
    }
  }

  getRefundTotalPointsApplied(): string {
    const totalRefundPoint = (this.props.contracts || []).filter(contract => contract.refundChecked).map(c => c.appliedPoints).reduce((total, cur) => (
      total + cur
    ), 0);
    return this.getFormattedPoint(totalRefundPoint);
  }

  renderEditBtn = () => {
    const { localize } = this.context;
    return (
      <MvLink label={localize("static.content.sr-edit")} onClick={this.props.onEditClick} />
    );
  }

  render() {
    const { localize } = this.context;

    // check if any applied points are borrowed
    const showOtherTable: boolean = this.props.resType !== ReservationType.CANCELED && this.props.contracts.some(c => c.borrowChecked);
    const showRefundTable: boolean = this.props.resType === ReservationType.CANCELED && this.props.contracts.some(c => c.refundChecked);

    return (
      <div className="usage-details-container">
        {" "}
        {/* <h3>{localize("static.content.usage-detail-header")}</h3><span>{this.renderEditBtn()}</span> */}
        <div className="usage-header">{localize("Usage Details")}<span>&nbsp;{!this.props.readOnly && this.renderEditBtn()}</span></div>
        <MvSortTable
          rowKey="usageId"
          headers={this.props.contracts.length > 0 ? this.tableHeaders(): []}
          rows={this.tableRows()}
          dataStatus={RequestStatus.LOADED} //TODO: send in summary review request status (passed into this compononent)?
          headerSection={
            <div className="usage-detail-header">
                <span className="usage-detail-label">
                  {(this.props.screenType === ScreenType.WAITLIST_CANCEL_SUMMARY_REVIEW) ? localize('Points returned') : (this.props.resType && this.props.resType === ReservationType.CANCELED) ? 
                    localize("static.content.usage-detail-points-prev-applied")+(this.props.screenType?" to waitlist":"")
                    :
                    localize("static.content.usage-detail-points-applied")+(this.props.screenType?" to waitlist":"")
                  }
                  {":"}&nbsp;
                </span>
                {this.getTotalPointsApplied()}
                {/* TODO: commenting out tool tip - will implement at a future date */}
                {/* {(this.props.resType === ReservationType.PAST || this.props.resType === ReservationType.CANCELED) &&
                  <div className='points-tooltip'>
                    <ToolTip
                      useRefToPosition={false}
                      tip={this.pointsTip}
                      tabIndex={0}
                      id='usage-details-points-tooltip'
                    >
                      <span className='question-icon'>{"?"}</span>
                    </ToolTip>
                  </div>
                }                 */}
            </div>
          }
          minWidth={1}
          columnStyles={{}}
          tableExpandable={this.props.contracts.length > 0 && this.props.tablesExpandable}
          tableExpanded={!this.props.tablesExpandable || this.state.tablesExpanded}
          hideRowBorder={true}
          hideBorder={true}
          apiTimeout = {this.props.apiTimeout}
        />
        {showOtherTable &&
          <div className='other-table-container'>
            <MvSortTable
              rowKey="usageId"
              headers={this.otherTableHeaders()}
              rows={this.otherTableRows()}
              dataStatus={RequestStatus.LOADED} //TODO: send in summary review request status (passed into this compononent)?
              headerSection={
                <div>
                  <span className='usage-detail-label'>
                      {localize("static.content.usage-detail-other-usages")}
                  </span>
                </div>
              }
              minWidth={1}
              columnStyles={{}}
              tableExpandable={this.props.tablesExpandable}
              tableExpanded={!this.props.tablesExpandable || this.state.tablesExpanded}
              hideRowBorder={true}
              hideBorder={true}
              apiTimeout = {this.props.apiTimeout}
            />
          </div>
        }
        {showRefundTable &&
          <div className='other-table-container'>
            <MvSortTable
              rowKey="usageId"
              headers={this.refundTableHeaders()}
              rows={this.refundTableRows()}
              dataStatus={RequestStatus.LOADED} //TODO: send in summary review request status (passed into this compononent)?
              headerSection={
                <div>
                  <span className='usage-detail-label'>
                    {localize("static.content.usage-detail-points-returned")}
                    {":"}&nbsp;
                  </span>
                  {this.getRefundTotalPointsApplied()}
                </div>
              }
              minWidth={1}
              columnStyles={{}}
              tableExpandable={this.props.tablesExpandable}
              tableExpanded={!this.props.tablesExpandable || this.state.tablesExpanded}
              hideRowBorder={true}
              hideBorder={true}
              apiTimeout = {this.props.apiTimeout}
            />
          </div>
        }
      </div>
    );
  }
}

UsageDetails.contextType = LocalizationContext;
export {
  UsageDetails,
};